import { useEffect } from "react";
import { useRouter } from "next/router";
import { useAtomValue } from "jotai";
import { userDataState } from "@/lib/store";
import { hasStoredCognitoSession } from "@/lib/storedSession";
import { OptionsUiUserData, optionsPlatformPrefetchHref, shouldLeaveLegacyOptions } from "./optionsUiRouting";

export type { OptionsUiUserData };
export { optionsPlatformPrefetchHref, shouldLeaveLegacyOptions };

/**
 * Old/new Options interface routing (paid-launch rules).
 *
 * The OLD options pages exist only for grandfathered users now
 * (PRODUCT_OPTIONS_LEGACY — signed OPRA before the paid launch):
 *
 *  - legacy pages call `useOptionsUiRedirect("legacy", newHref)`:
 *      · entitled users (paid addon or the backend-derived
 *        `new_options_ui` flag) are forwarded to the new interface;
 *      · users WITHOUT grandfathered access are ALSO forwarded — new
 *        users never see the old pages; the new platform's AddonGate +
 *        offer (first month free, then paid) is their only path.
 *      · only legacy users (without the addon) remain.
 *  - new-platform pages call `useOptionsUiRedirect("new", legacyHref)`:
 *    no bounce since the paid launch — anyone may view; AddonGate
 *    paywalls the content.
 *
 * Deliberately conservative: nothing happens until the user payload has
 * loaded AND carries the relevant flags (a backend that doesn't send
 * them yet — or a logged-out visitor — changes nothing).
 *
 * Staff are routed exactly like everyone else. There used to be a
 * bypass keeping them on the legacy pages for support/QA, but it made
 * every internal account unable to reach the shipped experience: an
 * entitled staff user clicking Options landed on the old interface and
 * could only see the platform by typing /options-platform/ by hand, so
 * the one routing path customers actually take was the one nobody
 * inside the company could exercise. Support can still open the legacy
 * pages deliberately — they are simply no longer the default.
 */

/**
 * Should a legacy options page render its content at all?
 *
 * The redirect is client-side and can only run once the user payload
 * has loaded, so a user destined for the new platform used to see the
 * OLD interface for a beat before being replaced out of it. A customer
 * who has paid for the new platform must never be shown the old one —
 * not even for half a second — so legacy pages hold their content until
 * the decision is known and render nothing when the answer is "leave".
 */
export function useLegacyOptionsVisible(): boolean {
    const userData = useAtomValue(userDataState) as OptionsUiUserData | null | undefined;
    return shouldLeaveLegacyOptions(userData) === false;
}

export function useOptionsUiRedirect(pageIs: "legacy" | "new", counterpartHref: string | null) {
    const userData = useAtomValue(userDataState) as OptionsUiUserData | null | undefined;
    const router = useRouter();

    // Decided on mount (and per page), before the user call returns: see optionsPlatformPrefetchHref.
    useEffect(() => {
        const href = optionsPlatformPrefetchHref(pageIs, counterpartHref, userData, hasStoredCognitoSession());
        if (href) router.prefetch(href).catch(() => {});
        // userData is read only for its value at mount; a later payload is the redirect's business.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageIs, counterpartHref]);

    useEffect(() => {
        if (!userData || !counterpartHref) return;
        if (pageIs === "legacy" && shouldLeaveLegacyOptions(userData) === true) {
            router.replace(counterpartHref);
        }
        // pageIs === "new": no bounce — AddonGate paywalls the content and
        // the OfferHost pitches the add-on (first month free).
        // router identity churns per render; path-relevant inputs only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, pageIs, counterpartHref]);
}
